import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';

import { View, Text, Image, StyleSheet, Dimensions } from 'react-native';
import { COLOR, FONT, FONT_SIZE } from '../constants/styles';
import { NavBar, Navigation, TouchableOpacity, CloseButton } from './components';

const TOP_STYLE_OPEN = { top: 0 };
const LEFT_STYLE_OPEN = { left: 0 };
const BACKGROUND_STYLE_OPEN = { opacity: 1 };

const DEFAULT_LEFT_BUTTONS = [
  {
    id: 'cancel',
    image: require('../../resources/arrow-left.png'),
  },
];
const DEFAULT_RIGHT_BUTTONS = [
  {
    id: 'cancel',
    defaultClose: true,
  },
];
const EMPTY_ARRAY = [];

const NavButton = memo(function NavButton(props) {
  const button = props.buttonConfig;
  const handlePress = useCallback(
    function handlePress() {
      Navigation.navigationButtonPressed({
        buttonId: props.buttonConfig.id,
      });
    },
    [props.buttonConfig]
  );
  if (props.buttonConfig.id === 'cancel' && props.buttonConfig.defaultClose) {
    return (
      <CloseButton
        buttonConfig={props.buttonConfig}
        autoFocus={props.autoFocus}
        animated={props.animated}
        onPress={handlePress}
      />
    );
  }
  return (
    <TouchableOpacity
      buttonConfig={props.buttonConfig}
      style={styles.navButton}
      autoFocus={props.autoFocus}
      animated={props.animated}
      onPress={handlePress}
    >
      {button.image ? (
        <Image source={button.image} style={[styles.navButtonImage, button.style || null]} />
      ) : (
        <Text style={styles.navButtonText}>{button.text}</Text>
      )}
    </TouchableOpacity>
  );
});

function topBar(options) {
  return options?.topBar
    ? options.topBar
    : {
        leftButtons: [NavBar.buttons.done],
      };
}
function title(options) {
  return options?.topBar?.title?.text || options.title;
}

const ModalScreenNew = memo((props) => {
  const [topStyle, setTopStyle] = useState({});
  const [leftStyle, setLeftStyle] = useState({});
  const [backgroundStyle, setBackgroundStyle] = useState({});
  const [closedStyle, setClosedStyle] = useState({});
  useEffect(() => {
    setBackgroundStyle(BACKGROUND_STYLE_OPEN);
    setTopStyle(TOP_STYLE_OPEN);
    setLeftStyle(LEFT_STYLE_OPEN);
  }, [props.asModal]);
  useEffect(() => {
    if (props.closing && props.isTop) {
      setTopStyle({});
      setLeftStyle({});
      setBackgroundStyle({});
    } else if (props.closing && !props.isTop) {
      setClosedStyle({ opacity: 0 });
    }
  }, [props.closing, props.isTop]);
  const _renderHeaderButton = (button, i, autoFocus = false) => {
    return (
      <NavButton
        buttonConfig={button}
        autoFocus={autoFocus}
        animated={true}
        key={`nv-button-${button.id}-${button.text}-${i}`}
      />
    );
  };
  const renderHeaderButton = (button, i) => {
    return _renderHeaderButton(button, i);
  };
  const renderHeaderButtonFocus = (button, i) => {
    return _renderHeaderButton(button, i, i === 0);
  };

  function renderHeader() {
    if (props.options?.noNavBar) {
      return null;
    }
    const topBarOptions = topBar(props.options);
    const titleText = title(props.options);
    const leftButtons =
      topBarOptions?.leftButtons?.length && props.asModal
        ? topBarOptions.leftButtons
        : !props.asModal
          ? DEFAULT_LEFT_BUTTONS
          : EMPTY_ARRAY;
    const rightButtons = topBarOptions?.rightButtons?.length
      ? topBarOptions?.rightButtons
      : !!props.asModal
        ? DEFAULT_RIGHT_BUTTONS
        : EMPTY_ARRAY;
    return (
      <View style={[styles.header, props.isTop ? null : { opacity: 0.9 }]}>
        <View style={styles.headerLeft}>{leftButtons.map(renderHeaderButtonFocus)}</View>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <Text style={styles.headerText}>{titleText}</Text>
        </View>
        <View style={styles.headerRight}>
          {rightButtons.map(leftButtons?.length ? renderHeaderButton : renderHeaderButtonFocus)}
        </View>
      </View>
    );
  }

  const dims = Dimensions.get('window');
  const perc = dims.width < 450 || dims.height < 450 ? '100%' : '90%';
  let containerStyle = {
    height: props.options?.height || perc,
    width: props.options?.width || perc,
    ...topStyle,
    ...leftStyle,
    ...closedStyle,
    ...(perc === '100%' ? { borderRadius: 0 } : null),
    ...(props.options?.containerStyle || null),
  };

  return (
    <View
      style={[
        styles.modalBackground,
        backgroundStyle,
        !props.isBottom ? { backgroundColor: 'transparent' } : null,
      ]}
    >
      <View
        style={[
          styles.container,
          props.asModal || props.closingAll ? styles.containerModal : styles.containerPush,
          containerStyle,
        ]}
      >
        {renderHeader()}
        <View style={[styles.screenContainer, props.options?.screenContainerStyle]}>
          <props.ScreenComponent {...props.passProps} />
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  modalBackground: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: COLOR.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    opacity: 0,
    transition: 'opacity 250ms ease-out',
  },
  container: {
    backgroundColor: COLOR.accent,
    borderWidth: 1,
    borderColor: COLOR.accent,
    borderRadius: 5,
    overflow: 'hidden',
    transition: 'top 250ms ease-out, left 250ms ease-out',
  },
  containerModal: {
    top: '100%',
  },
  containerPush: {
    left: '100%',
  },
  header: {
    backgroundColor: COLOR.altContrast,
    borderBottomColor: COLOR.accent,
    borderBottomWidth: 1,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    height: 46,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
    zIndex: 10,
  },
  headerLeft: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: 10,
    width: 100,
  },
  headerRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingRight: 10,
    width: 100,
  },
  headerText: {
    ...FONT.medium,
    fontSize: FONT_SIZE.small,
    textAlign: 'center',
    color: COLOR.text,
  },
  navButtonText: {
    ...FONT.medium,
    fontSize: FONT_SIZE.regular,
    textAlign: 'center',
    color: COLOR.callToAction,
  },
  navButtonImage: {
    width: 18,
    height: 12,
    resizeMode: 'contain',
    tintColor: 'rgb(123,123,123)',
  },
  screenContainer: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: 'white',
  },
  navButton: {
    borderRadius: 5,
    padding: 5,
  },
});

export default ModalScreenNew;
